import { z } from 'zod';
import { paginationSchema } from '@/types/pagination';

const commentAuthorRoleSchema = z.enum(['administrator', 'archivist', 'owner', 'guest']);

export const commentAuthorSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    role: commentAuthorRoleSchema.nullable().optional(),
  })
  .passthrough();

export type CommentAuthorRole = z.infer<typeof commentAuthorRoleSchema>;
export type CommentAuthor = z.infer<typeof commentAuthorSchema>;

export const CommentResolutionFilter = {
  RESOLVED: 'resolved',
  UNRESOLVED: 'unresolved',
} as const;

export type CommentResolutionFilterOption = (typeof CommentResolutionFilter)[keyof typeof CommentResolutionFilter];

export const commentResolutionFilterValues = Object.values(
  CommentResolutionFilter,
) as Array<CommentResolutionFilterOption>;

export const CommentVisibilityFilter = {
  PUBLIC: 'public',
  PRIVATE: 'private',
} as const;

export type CommentVisibilityFilterOption = (typeof CommentVisibilityFilter)[keyof typeof CommentVisibilityFilter];

export const commentVisibilityFilterValues = Object.values(
  CommentVisibilityFilter,
) as Array<CommentVisibilityFilterOption>;

// A standalone third filter, mutually exclusive with resolution/visibility: selecting it clears
// both of the others, and selecting either of the others clears it. Resolution and visibility
// remain independent of each other and can be combined.
export const CommentAllFilter = {
  ALL: 'all',
} as const;

export type CommentAllFilterOption = (typeof CommentAllFilter)[keyof typeof CommentAllFilter];

export const commentSchema = z
  .object({
    id: z.string(),
    body: z.string(),
    createdBy: commentAuthorSchema.nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    parentId: z.string().nullable(),
  })
  .passthrough();

type CommentBase = z.infer<typeof commentSchema>;

export type CommentTombstone = {
  isDeleted: true;
};

export type Comment = CommentBase & {
  tombstone?: CommentTombstone;
};

export const nodeCommentSchema = z.object({
  commentId: z.string(),
  nodeId: z.string(),
  comment: commentSchema,
  latestReplies: z.array(commentSchema),
  repliesCount: z.number(),
  resolvedAt: z.string().nullable(),
  resolvedBy: commentAuthorSchema.nullable(),
  private: z.boolean().nullable().optional(),
});

export const changeNodeCommentResolutionSchema = z.object({
  commentId: z.string(),
  nodeId: z.string(),
  resolvedAt: z.string().nullable(),
  resolvedBy: commentAuthorSchema.nullable(),
});

type NodeCommentBase = z.infer<typeof nodeCommentSchema>;

export type NodeComment = Omit<NodeCommentBase, 'comment' | 'latestReplies' | 'tombstone'> & {
  comment: Comment;
  latestReplies: Array<Comment>;
  tombstone?: CommentTombstone;
  deletedReplyIds?: Array<string>;
};

export const getNodeCommentsSchema = z.object({
  comments: z.array(nodeCommentSchema),
  pagination: paginationSchema,
});

type GetNodeCommentsResponseBase = z.infer<typeof getNodeCommentsSchema>;

export type GetNodeCommentsResponse = Omit<GetNodeCommentsResponseBase, 'comments'> & {
  comments: Array<NodeComment>;
};

export const getCommentRepliesSchema = z.object({
  comments: z.array(commentSchema),
  pagination: paginationSchema,
});

type GetCommentRepliesResponseBase = z.infer<typeof getCommentRepliesSchema>;

export type GetCommentRepliesResponse = Omit<GetCommentRepliesResponseBase, 'comments'> & {
  comments: Array<Comment>;
};

export const createNodeCommentSchema = commentSchema;

export type CreateNodeCommentResponse = z.infer<typeof createNodeCommentSchema>;
